import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useOrganisations } from '../hooks/useOrganisations';
import { downloadTemplate } from '../api';

interface OrganisationSelectProps {
  value: string | null;
  onChange: (id: string, name: string) => void;
  disabled?: boolean;
}

export function OrganisationSelect({
  value,
  onChange,
  disabled = false,
}: OrganisationSelectProps) {
  const { t } = useTranslation('customer-onboarding');
  const { data: organisations, isLoading, error } = useOrganisations();
  const [isDownloading, setIsDownloading] = useState(false);
  const [open, setOpen] = useState(false);

  const handleDownloadTemplate = async () => {
    setIsDownloading(true);
    try {
      await downloadTemplate();
      toast.success(t('toast.templateDownloaded'));
    } catch {
      toast.error(t('toast.templateFailed'));
    } finally {
      setIsDownloading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium">{t('organisation.label')}</label>
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-2">
        <label className="text-sm font-medium">{t('organisation.label')}</label>
        <div className="text-sm text-destructive">
          {t('common:error')}. {t('common:retry')}.
        </div>
      </div>
    );
  }

  const selectedOrg = organisations?.find((o) => o._id === value);

  return (
    <div className="flex items-end gap-4">
      <div className="flex-1 space-y-2">
        <label className="text-sm font-medium">
          {t('organisation.label')} <span className="text-destructive">*</span>
        </label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className="w-full justify-between font-normal"
            >
              {selectedOrg
                ? `${selectedOrg.name} (${selectedOrg.org_num})`
                : t('organisation.placeholder')}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder={t('organisation.search')} />
              <CommandList>
                <CommandEmpty>{t('organisation.noResults')}</CommandEmpty>
                <CommandGroup>
                  {organisations?.map((org) => (
                    <CommandItem
                      key={org._id}
                      value={`${org.name} ${org.org_num}`}
                      onSelect={() => {
                        onChange(org._id, org.name);
                        setOpen(false);
                      }}
                    >
                      <Check
                        className={cn(
                          'mr-2 h-4 w-4',
                          value === org._id ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                      {org.name} ({org.org_num})
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      <Button
        type="button"
        variant="outline"
        onClick={handleDownloadTemplate}
        disabled={isDownloading}
        className="shrink-0"
      >
        {isDownloading ? (
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
        ) : (
          <Download className="mr-2 h-4 w-4" />
        )}
        {t('organisation.downloadTemplate')}
      </Button>
    </div>
  );
}
